import { PgTypeIcon } from "../icons/PgTypeIcon";
import FilterValueField from "./FilterValueField";
import { DatabaseConnectionSchema } from "@avenueops/shared-types";
import { Column, PostgresQueryCompiler, WhereFilter, WhereFilterValueType, WhereOperator } from "@avenueops/visual-query-editor";
import { OptionGroup, SmartForm } from "@avenueops/web-components";
import { useFormContext } from "react-hook-form";

const valueTypeOptions = Object.values(WhereFilterValueType).map((valueType) => ({
  value: valueType,
  label: valueType,
  icon: <PgTypeIcon pgType={valueType} className="w-4 h-4" />,
}));

interface Props {
  prefix: string;
  columnOptions: OptionGroup[];
  connectionSchema?: DatabaseConnectionSchema;
}

export default function Filter({ prefix, columnOptions, connectionSchema }: Props) {
  const { setValue, watch } = useFormContext();
  const filter = watch(prefix) as WhereFilter;
  const leftColumn = watch(`${prefix}.left`) as Column;
  if (!leftColumn || !leftColumn.type) {
    return null;
  }
  const leftColumnType = PostgresQueryCompiler.getTsTypeFromType(leftColumn.type);
  const filteredTypeOptions = valueTypeOptions.filter((option) => option.value === leftColumnType || option.value === WhereFilterValueType.COLUMN);
  switch (filter.operator) {
    case WhereOperator.EQUALS:
    case WhereOperator.NOT_EQUALS:
    case WhereOperator.GREATER_THAN:
    case WhereOperator.GREATER_THAN_OR_EQUAL:
    case WhereOperator.LESS_THAN:
    case WhereOperator.LESS_THAN_OR_EQUAL:
      return (
        <>
          <div className="col-span-5 flex items-end">
            <div className="bg-gray-100 flex items-center justify-center my-1 w-16 h-10 border rounded-l border-cool-gray-300">
              <div>
                <SmartForm.DropdownSelect
                  name={`${prefix}.right.type`}
                  type="INLINE"
                  hideSelectedLabel
                  hideSearchBar
                  onChangeCallback={() => {
                    setValue(`${prefix}.right.value`, null, {
                      shouldValidate: false,
                      shouldDirty: true,
                    });
                  }}
                  options={leftColumnType ? filteredTypeOptions : valueTypeOptions}
                  usePortal
                />
              </div>
            </div>
            <div className={`[&_.border]:border-l-0 [&_.border]:rounded-l-none w-full`}>
              <FilterValueField
                type={filter.right?.type}
                prefix={`${prefix}.right`}
                columOptions={columnOptions}
                connectionSchema={connectionSchema}
                leftColumn={leftColumn}
              />
            </div>
          </div>
        </>
      );
    case WhereOperator.NOT_LIKE:
    case WhereOperator.LIKE:
      if (!filter.right || filter.right.type !== WhereFilterValueType.STRING) {
        setValue(`${prefix}.right.type`, WhereFilterValueType.STRING, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
      return (
        <div className="col-span-5 flex items-end">
          <div className="bg-gray-100 flex items-center justify-center my-1 w-16 h-10 border rounded-l border-cool-gray-300">
            <div>
              <SmartForm.DropdownSelect
                hideSearchBar
                type="INLINE"
                hideSelectedLabel
                name={`${prefix}.right.type`}
                options={leftColumnType ? filteredTypeOptions : valueTypeOptions}
                disabled={true}
                onChangeCallback={() => {
                  setValue(`${prefix}.right.value`, null, {
                    shouldValidate: false,
                    shouldDirty: true,
                  });
                }}
                usePortal
              />
            </div>
          </div>
          <div className={`[&_.border]:border-l-0 [&_.border]:rounded-l-none w-full`}>
            <FilterValueField type={filter.right?.type} prefix={`${prefix}.right`} columOptions={columnOptions} />
          </div>
        </div>
      );
    case WhereOperator.NOT_BETWEEN:
    case WhereOperator.BETWEEN:
      return (
        <>
          <div className="col-span-2 flex items-end">
            <div className="bg-gray-100 flex items-center justify-center my-1 w-16 h-10 border rounded-l border-cool-gray-300">
              <div>
                <SmartForm.DropdownSelect
                  hideSearchBar
                  type="INLINE"
                  hideSelectedLabel
                  onChangeCallback={() => {
                    setValue(`${prefix}.right.value`, null, {
                      shouldValidate: false,
                      shouldDirty: true,
                    });
                  }}
                  name={`${prefix}.right.type`}
                  options={leftColumnType ? filteredTypeOptions : valueTypeOptions}
                  usePortal
                />
              </div>
            </div>
            <div className={`[&_.border]:border-l-0 [&_.border]:rounded-l-none w-full`}>
              <FilterValueField type={filter.right?.type} prefix={`${prefix}.right`} columOptions={columnOptions} />
            </div>
          </div>

          <div className="flex justify-center items-center">
            <span className="text-gray-700 ">and</span>
          </div>
          <div className="col-span-2 flex items-end">
            <div className="bg-gray-100 flex items-center justify-center my-1 w-16 h-10 border rounded-l border-cool-gray-300">
              <div>
                <SmartForm.DropdownSelect
                  hideSearchBar
                  type="INLINE"
                  hideSelectedLabel
                  name={`${prefix}.rightSecond.type`}
                  onChangeCallback={() => {
                    setValue(`${prefix}.rightSecond.value`, null, {
                      shouldValidate: false,
                      shouldDirty: true,
                    });
                  }}
                  options={leftColumnType ? filteredTypeOptions : valueTypeOptions}
                  usePortal
                />
              </div>
            </div>
            <div className={`[&_.border]:border-l-0 [&_.border]:rounded-l-none w-full`}>
              <FilterValueField type={filter.rightSecond?.type} prefix={`${prefix}.rightSecond`} columOptions={columnOptions} />
            </div>
          </div>
        </>
      );
    case WhereOperator.IS_NULL:
    case WhereOperator.IS_NOT_NULL:
    default:
      return null;
  }
}
